import { memo, useCallback, useRef } from "react"
import styled from "styled-components"
import SelectV2, { Option } from "components/EvtKit/select/SelectV2"
import DatePicker from "components/EvtKit/date-picker/DatePicker"
import { PeriodFilters, periodsLabels } from "constants/metrics"
import { getDateRangeByPeriod } from "utils/formatDate"
import Caption from "components/Typography/Caption"
import ReactDatePicker from "react-datepicker"

const PeriodDatePickerContainer = styled.div`
  max-width: 228px;
  width: 100%;
  display: flex;
  position: relative;

  .react-datepicker__children-container {
    padding: 0;
  }
`

const CalendarFooter = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1px;
`

const Divider = styled.div`
  border: 1px solid ${(props) => props.theme.palette.primary_one};
  width: 100%;
  opacity: 0.3;
`

const CalendarOptions = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 0px 6px 0px;
  justify-content: flex-end;

  .calendar-option {
    cursor: pointer;
  }
`

interface PeriodDatePickerProps {
  periodFilter: PeriodFilters
  dateStart?: Date
  dateEnd?: Date
  onPeriodChange: (period: PeriodFilters) => void
  onDateChange: (dates: [Date | null, Date | null]) => void
  className?: string
}

const PeriodDatePicker = (props: PeriodDatePickerProps) => {
  const { periodFilter, dateStart, dateEnd, onPeriodChange, onDateChange, className } = props

  const datePickerRef = useRef<ReactDatePicker | null>(null)

  const handlePeriodChange = useCallback(
    (option: Option | null) => {
      const newPeriod = (option?.value as PeriodFilters) || PeriodFilters.THIRTY_DAYS

      onPeriodChange(newPeriod)

      if (newPeriod === PeriodFilters.CUSTOM) {
        onDateChange([null, null])

        return
      }

      const dateRange = getDateRangeByPeriod(newPeriod)

      onDateChange(dateRange)
    },
    [onPeriodChange, onDateChange]
  )

  const handleDateChange = useCallback(
    (dates: Date | [Date | null, Date | null] | null) => {
      if (Array.isArray(dates)) {
        onDateChange(dates)
      }
    },
    [onDateChange]
  )

  const handleBackToPeriodSelect = useCallback(() => {
    if (datePickerRef.current?.setOpen) {
      datePickerRef.current.setOpen(false)
    }

    onPeriodChange(PeriodFilters.THIRTY_DAYS)
    const defaultRange = getDateRangeByPeriod(PeriodFilters.THIRTY_DAYS)

    onDateChange(defaultRange)
  }, [onPeriodChange, onDateChange])

  const handleCloseCalendar = useCallback(() => {
    if (datePickerRef.current?.setOpen) {
      datePickerRef.current.setOpen(false)
    }
  }, [])

  return (
    <PeriodDatePickerContainer className={className}>
      {periodFilter !== PeriodFilters.CUSTOM && (
        <SelectV2
          id="period-filter"
          label="Período"
          onChange={handlePeriodChange}
          placeholder="Selecione o Período"
          shrink
          value={periodFilter}
          className="fullWidth"
          options={periodsLabels}
        />
      )}

      {periodFilter === PeriodFilters.CUSTOM && (
        <DatePicker
          ref={datePickerRef}
          dateFormat="dd/MM/yyyy"
          onChange={handleDateChange}
          startDate={dateStart}
          endDate={dateEnd}
          selectsRange
          placeholderText="Selecione (De - Até)"
          label="Período"
          shrink
          id="custom-period"
          maxDays={29}
          startOpen
        >
          <CalendarFooter>
            <Divider />
            <CalendarOptions>
              <Caption
                color="primary-dark"
                weight="semi-bold"
                size="large"
                onClick={handleBackToPeriodSelect}
                className="calendar-option"
              >
                Voltar
              </Caption>
              <Caption
                color="primary-dark"
                weight="semi-bold"
                size="large"
                onClick={handleCloseCalendar}
                className="calendar-option"
              >
                Fechar
              </Caption>
            </CalendarOptions>
          </CalendarFooter>
        </DatePicker>
      )}
    </PeriodDatePickerContainer>
  )
}

export default memo(PeriodDatePicker)
